import React, { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import { signInWithPhoneNumber } from "firebase/auth";
import { useNavigate, useParams } from "react-router-dom";
import Back from "../assets/images/backArrow.svg";
import checkIcon from "../assets/images/check_circle.png";
import WhatHappensNext from "../components/WhatHappensNext";
import FailModel from "../components/Models/failModel";
import {
  buildPackageReview,
  createPackageLead,
  createPackageOrder,
  fetchPurchasePackages,
  loginWithIdToken,
  lookupPackagePatients,
  verifyPackagePayment,
} from "../api/packageApi";
import {
  createPackageRecaptchaVerifier,
  hasFirebasePackageConfig,
  packageAuth,
} from "../lib/firebasePackage";

const FREQUENCY_OPTIONS = [
  { label: "Daily", value: 7, helper: "every day" },
  { label: "Alternate days", value: 4, helper: "every other day" },
  { label: "3 days / week", value: 3, helper: "three sessions" },
  { label: "2 days / week", value: 2, helper: "two sessions" },
  { label: "Custom", value: "custom", helper: "your cadence" },
];

const TERMS = [
  "Package activation happens only after successful Razorpay payment verification.",
  "If you already have an active package, the new package will remain queued until the current one is completed.",
  "The final treatment schedule remains subject to therapist availability and clinical suitability.",
];

const GOOGLE_MAPS_LIBRARIES = ["places"];
const FLOW_STORAGE_KEY = "package-booking-flow-v1";
const STEP_TO_ROUTE_SEGMENT = {
  mobile: "login",
  otp: "otp",
  "patient-select": "select-patient",
  lead: "lead-details",
  packages: "select-package",
  review: "review",
  success: "success",
};
const ROUTE_SEGMENT_TO_STEP = Object.fromEntries(
  Object.entries(STEP_TO_ROUTE_SEGMENT).map(([stepKey, segment]) => [segment, stepKey])
);

const initialLeadForm = {
  name: "",
  city: "",
  pincode: "",
};

const initialReviewForm = {
  patient_name: "",
  city: "",
  pincode: "",
  address: "",
  notes: "",
};

const normalizePhone = (value) => String(value || "").replace(/\D+/g, "").slice(-10);

const formatPhoneForApi = (value) => `+91${normalizePhone(value)}`;

const formatPhoneDisplay = (value) => {
  const digits = String(value || "").replace(/\D+/g, "");
  if (!digits) return "--";
  if (digits.startsWith("91") && digits.length === 12) {
    return `+${digits}`;
  }
  return `+91${digits.slice(-10)}`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "--";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const todayInputValue = () => new Date().toISOString().split("T")[0];

const readStoredFlowDraft = () => {
  try {
    const raw = window.sessionStorage.getItem(FLOW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector('script[data-razorpay="checkout"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true), { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Unable to load Razorpay checkout")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.dataset.razorpay = "checkout";
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Unable to load Razorpay checkout"));
    document.body.appendChild(script);
  });

const PackagePurchaseFlow = () => {
  const navigate = useNavigate();
  const { flowStep } = useParams();
  const recaptchaRef = useRef(null);
  const didHydrateRef = useRef(false);
  const confirmationResultRef = useRef(null);
  const [step, setStep] = useState("mobile");
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loginSnapshot, setLoginSnapshot] = useState(null);
  const [lookupSnapshot, setLookupSnapshot] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [leadForm, setLeadForm] = useState(initialLeadForm);
  const [leadContext, setLeadContext] = useState(null);
  const [packages, setPackages] = useState([]);
  const [packagesLoaded, setPackagesLoaded] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [startDate, setStartDate] = useState(todayInputValue());
  const [frequencyChoice, setFrequencyChoice] = useState(4);
  const [customSessionsPerWeek, setCustomSessionsPerWeek] = useState(3);
  const [reviewData, setReviewData] = useState(null);
  const [reviewForm, setReviewForm] = useState(initialReviewForm);
  const [successData, setSuccessData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailModel, setShowFailModel] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("");
  const [showRecaptchaShell, setShowRecaptchaShell] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [addressAutocomplete, setAddressAutocomplete] = useState(null);

  const firebaseMissing = !hasFirebasePackageConfig || !packageAuth;
  const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
  const googleMapsApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyBqKbHptbwiV5W2wsYy0J1G1x2AKk2aeRU";
  const { isLoaded: isPlacesLoaded } = useLoadScript({
    googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const sessionsPerWeek =
    frequencyChoice === "custom" ? Number(customSessionsPerWeek) : Number(frequencyChoice);

  const activeActor = selectedPatient || leadContext || reviewData?.review?.patient || reviewData?.review?.lead;
  const isAuthStep = step === "mobile" || step === "otp";
  const routeStep = flowStep ? ROUTE_SEGMENT_TO_STEP[flowStep] || null : null;

  const clearStoredFlow = useCallback(() => {
    window.sessionStorage.removeItem(FLOW_STORAGE_KEY);
  }, []);

  const buildStepUrl = useCallback(
    (nextStep) => `/package-booking/${STEP_TO_ROUTE_SEGMENT[nextStep] || STEP_TO_ROUTE_SEGMENT.mobile}`,
    []
  );

  const canResumeStep = useCallback((nextStep, source = {}) => {
    switch (nextStep) {
      case "mobile":
        return true;
      case "otp":
        return Boolean(source.mobileNumber);
      case "patient-select":
        return Array.isArray(source.patients) && source.patients.length > 1;
      case "lead":
        return Boolean(source.mobileNumber) && Boolean(
          source.leadContext ||
          source.leadForm?.name ||
          source.leadForm?.city ||
          source.leadForm?.pincode
        );
      case "packages":
        return Boolean(source.selectedPatient || source.leadContext);
      case "review":
        return Boolean(source.reviewData);
      case "success":
        return Boolean(source.successData);
      default:
        return false;
    }
  }, []);

  const ensurePackagesLoaded = useCallback(async () => {
    if (packagesLoaded) return;

    const response = await fetchPurchasePackages();
    setPackages(response.data?.data || []);
    setPackagesLoaded(true);
  }, [packagesLoaded]);

  const handleAddressAutocompleteLoad = useCallback((instance) => {
    setAddressAutocomplete(instance);
  }, []);

  const handleAddressPlaceChanged = useCallback(() => {
    if (!addressAutocomplete) return;

    const place = addressAutocomplete.getPlace();
    if (place?.formatted_address) {
      setReviewForm((prev) => ({ ...prev, address: place.formatted_address }));
    }
  }, [addressAutocomplete]);

  useEffect(() => {
    return () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (step !== "otp" || resendCooldown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setResendCooldown((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [step, resendCooldown]);

  useEffect(() => {
    if (didHydrateRef.current) return;

    const draft = readStoredFlowDraft();
    const draftSource = {
      mobileNumber: draft?.mobileNumber || "",
      confirmationResult: null,
      patients: draft?.patients || [],
      leadForm: draft?.leadForm || initialLeadForm,
      leadContext: draft?.leadContext || null,
      selectedPatient: draft?.selectedPatient || null,
      reviewData: draft?.reviewData || null,
      successData: draft?.successData || null,
    };

    if (draft) {
      setMobileNumber(draft.mobileNumber || "");
      setLoginSnapshot(draft.loginSnapshot || null);
      setLookupSnapshot(draft.lookupSnapshot || null);
      setPatients(draft.patients || []);
      setSelectedPatient(draft.selectedPatient || null);
      setLeadForm(draft.leadForm || initialLeadForm);
      setLeadContext(draft.leadContext || null);
      setPackages(draft.packages || []);
      setPackagesLoaded(Boolean(draft.packagesLoaded || (draft.packages || []).length));
      setSelectedPackageId(draft.selectedPackageId || "");
      setStartDate(draft.startDate || todayInputValue());
      setFrequencyChoice(draft.frequencyChoice ?? 4);
      setCustomSessionsPerWeek(draft.customSessionsPerWeek ?? 3);
      setReviewData(draft.reviewData || null);
      setReviewForm(draft.reviewForm || initialReviewForm);
      setSuccessData(draft.successData || null);
    }

    const requestedStep = routeStep || draft?.step || "mobile";
    const nextStep = canResumeStep(requestedStep, draftSource) ? requestedStep : "mobile";

    setStep(nextStep);
    didHydrateRef.current = true;

    if (routeStep !== nextStep) {
      navigate(buildStepUrl(nextStep), { replace: true });
    }
  }, [buildStepUrl, canResumeStep, navigate, routeStep]);

  useEffect(() => {
    if (!didHydrateRef.current) return;

    const expectedUrl = buildStepUrl(step);
    if (window.location.pathname !== expectedUrl) {
      navigate(expectedUrl, { replace: true });
    }
  }, [
    buildStepUrl,
    navigate,
    step,
  ]);

  useEffect(() => {
    if (!didHydrateRef.current) return;

    window.sessionStorage.setItem(
      FLOW_STORAGE_KEY,
      JSON.stringify({
        step,
        mobileNumber,
        loginSnapshot,
        lookupSnapshot,
        patients,
        selectedPatient,
        leadForm,
        leadContext,
        packages,
        packagesLoaded,
        selectedPackageId,
        startDate,
        frequencyChoice,
        customSessionsPerWeek,
        reviewData,
        reviewForm,
        successData,
      })
    );
  }, [
    customSessionsPerWeek,
    frequencyChoice,
    leadContext,
    leadForm,
    loginSnapshot,
    lookupSnapshot,
    mobileNumber,
    packages,
    packagesLoaded,
    patients,
    reviewData,
    reviewForm,
    selectedPackageId,
    selectedPatient,
    startDate,
    step,
    successData,
  ]);

  useEffect(() => {
    if (!didHydrateRef.current) return;
    if (!["packages", "review", "success"].includes(step)) return;
    if (packagesLoaded) return;

    ensurePackagesLoaded().catch(() => {
      // Preserve the current screen and surface the normal error path later if needed.
    });
  }, [ensurePackagesLoaded, packagesLoaded, step]);

  const resetError = () => setErrorMessage("");
  const closeFailModel = () => setShowFailModel(false);
  const openPaymentFailModel = (message) => {
    setErrorMessage(message);
    setShowFailModel(true);
  };

  const setBusy = (label) => {
    setLoadingLabel(label);
    resetError();
  };

  const clearBusy = () => setLoadingLabel("");

  const resetRecaptchaShell = () => {
    setShowRecaptchaShell(false);
    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
      recaptchaRef.current = null;
    }
  };

  const handleBack = () => {
    resetError();

    if (step === "otp") {
      resetRecaptchaShell();
      setResendCooldown(0);
      setStep("mobile");
      return;
    }

    if (step === "patient-select") {
      setStep("otp");
      return;
    }

    if (step === "lead") {
      setStep("otp");
      return;
    }

    if (step === "packages") {
      if (selectedPatient) {
        setStep(patients.length > 1 ? "patient-select" : "otp");
      } else {
        setStep("lead");
      }
      return;
    }

    if (step === "review") {
      setStep("packages");
      return;
    }

    if (step === "success") {
      clearStoredFlow();
      window.location.reload();
    }
  };

  const handleSendOtp = async () => {
    const normalized = normalizePhone(mobileNumber);

    if (firebaseMissing) {
      setErrorMessage("Firebase web OTP is not configured yet.");
      return;
    }

    if (normalized.length !== 10) {
      setErrorMessage("Enter a valid 10 digit mobile number.");
      return;
    }

    try {
      setBusy("Sending OTP...");
      setMobileNumber(normalized);
      setShowRecaptchaShell(true);

      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }

      recaptchaRef.current = createPackageRecaptchaVerifier("package-recaptcha-container", {
        onExpired: () => setErrorMessage("reCAPTCHA expired. Please request OTP again."),
      });

      const confirmation = await signInWithPhoneNumber(
        packageAuth,
        formatPhoneForApi(normalized),
        recaptchaRef.current
      );

      setConfirmationResult(confirmation);
      confirmationResultRef.current = confirmation;

      setShowRecaptchaShell(false);
      setResendCooldown(30);
      setOtp("");
      setStep("otp");

      navigate(buildStepUrl("otp"), { replace: true });
    } catch (error) {
      setErrorMessage(error?.message || "Unable to send OTP right now.");
    } finally {
      clearBusy();
    }
  };

  const handleVerifyOtp = async () => {
    const activeConfirmation = confirmationResult || confirmationResultRef.current;

    console.log("VERIFY CLICKED", {
      otp,
      mobileNumber,
      hasConfirmationState: Boolean(confirmationResult),
      hasConfirmationRef: Boolean(confirmationResultRef.current),
    });

    if (!activeConfirmation) {
      console.log("FAILED: No Firebase confirmation object");
      setErrorMessage("Request OTP first.");
      return;
    }

    if (otp.trim().length !== 6) {
      console.log("FAILED: Invalid OTP length", otp);
      setErrorMessage("Enter the 6 digit OTP.");
      return;
    }

    try {
      setBusy("Verifying OTP...");

      console.log("STEP 1: Confirming OTP with Firebase...");
      const credential = await activeConfirmation.confirm(otp.trim());
      console.log("STEP 1 SUCCESS:", credential);

      console.log("STEP 2: Getting Firebase ID token...");
      const idToken = await credential.user.getIdToken();
      console.log("STEP 2 SUCCESS:", idToken);

      console.log("STEP 3: Calling loginWithIdToken API...");
      const loginResponse = await loginWithIdToken(idToken);
      console.log("STEP 3 SUCCESS:", loginResponse.data);

      console.log("STEP 4: Calling lookupPackagePatients API...");
      const lookupResponse = await lookupPackagePatients(formatPhoneForApi(mobileNumber));
      console.log("STEP 4 SUCCESS:", lookupResponse.data);

      // keep rest of your existing code from here
      setLoginSnapshot(loginResponse.data);
      setLookupSnapshot(lookupResponse.data);

      const nextPatients = lookupResponse.data?.patients || [];
      const nextPatient = lookupResponse.data?.patient || null;
      const nextLead = lookupResponse.data?.lead || null;
      const resolution = lookupResponse.data?.resolution;
      const isProvisionalLeadPatient =
        String(nextPatient?.user_status || "").trim().toLowerCase() === "lead";

      setPatients(nextPatients);

      if (resolution === "single_patient" && nextPatient && !isProvisionalLeadPatient) {
        setSelectedPatient(nextPatient);
        setLeadContext(null);
        await ensurePackagesLoaded();
        setStep("packages");
        return;
      }

      if (resolution === "multiple_patients") {
        setSelectedPatient(null);
        setLeadContext(null);
        setStep("patient-select");
        navigate(buildStepUrl("patient-select"), { replace: true });
        return;
      }

      if (nextLead?.lead_id || (resolution === "single_patient" && isProvisionalLeadPatient)) {
        setSelectedPatient(null);
        setLeadContext(nextLead);
        setLeadForm({
          name: nextLead?.name || nextPatient?.name || "",
          city: nextLead?.city || nextPatient?.city || "",
          pincode: nextLead?.pincode || nextPatient?.pincode || "",
        });
        setStep("lead");
        return;
      }

      setSelectedPatient(null);
      setLeadContext(null);
      setLeadForm(initialLeadForm);
      setStep("lead");
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || "OTP verification failed.");
    } finally {
      clearBusy();
    }
  };

  const handleLeadCreate = async () => {
    if (!leadForm.name.trim() || !leadForm.city.trim() || !leadForm.pincode.trim()) {
      setErrorMessage("Name, city and pincode are required.");
      return;
    }

    try {
      setBusy("Saving lead...");
      const response = await createPackageLead({
        phone_number: formatPhoneForApi(mobileNumber),
        name: leadForm.name.trim(),
        city: leadForm.city.trim(),
        pincode: leadForm.pincode.trim(),
      });

      setLeadContext(response.data?.lead || null);
      await ensurePackagesLoaded();
      setStep("packages");
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Unable to save lead details.");
    } finally {
      clearBusy();
    }
  };

  const handlePatientSelection = async (patient) => {
    setSelectedPatient(patient);
    setLeadContext(null);
    await ensurePackagesLoaded();
    setStep("packages");
  };

  const handleBuildReview = async () => {
    if (!selectedPackageId) {
      setErrorMessage("Select a package first.");
      return;
    }

    if (!startDate) {
      setErrorMessage("Choose a start date.");
      return;
    }

    if (!sessionsPerWeek || Number(sessionsPerWeek) <= 0) {
      setErrorMessage("Choose a valid session frequency.");
      return;
    }

    try {
      setBusy("Preparing review...");
      const response = await buildPackageReview({
        patient_id: selectedPatient?.patient_id,
        lead_id: leadContext?.lead_id,
        package_detail_id: selectedPackageId,
        start_date: startDate,
        sessions_per_week: sessionsPerWeek,
        frequency_label:
          frequencyChoice === "custom"
            ? `Custom (${sessionsPerWeek} sessions/week)`
            : FREQUENCY_OPTIONS.find((item) => item.value === frequencyChoice)?.label,
      });

      setReviewData(response.data);
      const reviewActor = response.data?.review?.patient || response.data?.review?.lead || {};
      setReviewForm((prev) => ({
        ...prev,
        patient_name: prev.patient_name || reviewActor.name || "",
        city: prev.city || reviewActor.city || "",
        pincode: prev.pincode || reviewActor.pincode || "",
        address: prev.address || reviewActor.address || "",
      }));
      setStep("review");
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Unable to build review.");
    } finally {
      clearBusy();
    }
  };

  const handlePayment = async () => {
    if (!reviewForm.patient_name.trim()) {
      setErrorMessage("Patient name is required before payment.");
      return;
    }

    if (!reviewForm.city.trim()) {
      setErrorMessage("City is required before payment.");
      return;
    }

    if (reviewForm.pincode.trim().length !== 6) {
      setErrorMessage("Enter a valid 6 digit pincode before payment.");
      return;
    }

    if (!reviewForm.address.trim()) {
      setErrorMessage("Address is required before payment.");
      return;
    }

    if (!razorpayKey) {
      setErrorMessage("Razorpay key is not configured.");
      return;
    }

    try {
      setBusy("Creating payment order...");

      const orderResponse = await createPackageOrder({
        patient_id: selectedPatient?.patient_id,
        lead_id: leadContext?.lead_id,
        package_detail_id: reviewData?.review?.package?.id,
        start_date: reviewData?.review?.schedule?.start_date || startDate,
        sessions_per_week: reviewData?.review?.schedule?.sessions_per_week || sessionsPerWeek,
        frequency_label: reviewData?.review?.schedule?.frequency_label,
        patient_name: reviewForm.patient_name.trim(),
        city: reviewForm.city.trim(),
        pincode: reviewForm.pincode.trim(),
        address: reviewForm.address.trim(),
        notes: reviewForm.notes.trim(),
      });

      await loadRazorpayScript();

      const order = orderResponse.data?.data?.order;
      const packagePurchaseId = orderResponse.data?.data?.package_purchase_id;
      const patient = orderResponse.data?.data?.patient;

      if (!order?.id || !packagePurchaseId) {
        throw new Error("Incomplete payment order response");
      }

      const razorpay = new window.Razorpay({
        key: razorpayKey,
        amount: order.amount,
        currency: order.currency,
        name: "Physica",
        description: `${reviewData?.review?.package?.package_name} package`,
        order_id: order.id,
        prefill: {
          name: patient?.name || activeActor?.name || "",
          contact: normalizePhone(patient?.phone_number || activeActor?.phone_number || mobileNumber),
        },
        theme: {
          color: "#FE8235",
        },
        modal: {
          ondismiss: () => {
            clearBusy();
            openPaymentFailModel("The payment window was closed before the package purchase could be completed.");
          },
        },
        handler: async (paymentResponse) => {
          try {
            setBusy("Verifying payment...");
            const verificationResponse = await verifyPackagePayment({
              package_purchase_id: packagePurchaseId,
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
            });

            setSuccessData(verificationResponse.data?.data || null);
            setStep("success");
          } catch (error) {
            openPaymentFailModel(
              error?.response?.data?.message ||
              "The payment was received, but we could not verify it automatically. Please try again or contact our team if the amount was debited."
            );
          } finally {
            clearBusy();
          }
        },
      });

      razorpay.on("payment.failed", (response) => {
        clearBusy();
        openPaymentFailModel(
          response?.error?.description ||
          "The package payment could not be completed. Please check your payment method and try again."
        );
      });

      razorpay.open();
    } catch (error) {
      openPaymentFailModel(
        error?.response?.data?.message ||
        error?.message ||
        "We could not start the package payment right now. Please try again."
      );
      clearBusy();
    }
  };

  const renderMobileStep = () => {
    const otpVisible = step === "otp";

    return (
      <SectionCard>
        <div className="relative mx-auto max-w-[372px] px-2 pb-3 pt-1">
          <div className="pointer-events-none absolute bottom-[-4px] left-[-16px] h-24 w-24 rounded-full bg-[radial-gradient(circle,rgba(254,130,53,0.12),transparent_72%)]" />

          <div className="relative">
            <h2 className="text-[33px] font-semibold leading-[0.98] tracking-[-0.05em] text-[#171717]">
              Start your <span className="text-[#e56f1c]">package</span>
              <br />
              booking
            </h2>
            <p className="mt-4 max-w-[300px] text-[16px] leading-6 text-[#5f544c]">
              We&apos;ll look up your profile using your mobile number and get you set up in seconds.
            </p>

            <div className="mt-7 rounded-[22px] border border-[#ead8ca] bg-white px-3 py-5 shadow-[0_10px_30px_rgba(77,54,35,0.04)] sm:px-4">
              <div className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#8a6a56]">
                Mobile Number
              </div>

              <div className="mt-3 flex items-center gap-3 rounded-[16px] border border-[#efb08b] bg-[#fffaf6] px-4 py-3">
                <div className="shrink-0 text-[16px] font-medium text-[#342e2a]">+91</div>
                <input
                  type="text"
                  value={mobileNumber}
                  onChange={(event) => setMobileNumber(normalizePhone(event.target.value))}
                  placeholder="10-digit number"
                  inputMode="numeric"
                  maxLength={10}
                  className="min-w-0 flex-1 bg-transparent text-[16px] font-medium text-[#1f1f1f] outline-none placeholder:text-[#9b938d]"
                />
              </div>

              <div className="mt-3 flex items-center gap-2 text-[11px] text-[#7a6658]">
                <span className="inline-flex h-3.5 w-3.5 items-center justify-center text-[#b89479]">
                  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.3">
                    <rect x="4.5" y="6" width="7" height="5.5" rx="1.2" />
                    <path d="M6 6V4.8A2 2 0 0 1 8 2.8a2 2 0 0 1 2 2V6" />
                  </svg>
                </span>
                <span>We&apos;ll send a one-time password to this number</span>
              </div>

              {otpVisible ? (
                <div className="mt-4 rounded-[18px] border border-[#f0dfd4] bg-[linear-gradient(180deg,#fffdfa_0%,#fff7f1_100%)] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-[#1d1d1d]">OTP verification</div>
                      <div className="mt-1 text-[12px] text-[#6d635b]">
                        Code sent to {formatPhoneDisplay(mobileNumber)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        resetRecaptchaShell();
                        setResendCooldown(0);
                        setStep("mobile");
                        setOtp("");
                      }}
                      className="text-xs font-semibold text-[#d86a1b]"
                    >
                      Change
                    </button>
                  </div>

                  <input
                    type="text"
                    value={otp}
                    onChange={(event) => setOtp(event.target.value.replace(/\D+/g, "").slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    inputMode="numeric"
                    maxLength={6}
                    className="mt-4 w-full rounded-[14px] border border-[#e4d8d0] bg-white px-4 py-3 text-center text-[17px] font-semibold tracking-[0.28em] text-[#1f1f1f] outline-none transition placeholder:text-[13px] placeholder:tracking-[0.08em] focus:border-[#FE8235] focus:shadow-[0_0_0_4px_rgba(254,130,53,0.08)]"
                  />

                  <div className="mt-4 flex gap-3">
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={Boolean(loadingLabel) || resendCooldown > 0}
                      className="basis-[38%] rounded-[14px] border border-[#d8d1ca] bg-white px-3 py-3 text-sm font-semibold text-[#3b3632] transition hover:border-[#FE8235] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {resendCooldown > 0 ? `${resendCooldown}s` : "Resend"}
                    </button>
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={Boolean(loadingLabel)}
                      className="basis-[66%] rounded-[14px] bg-[linear-gradient(90deg,#ffb17a_0%,#FE8235_100%)] px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_28px_rgba(254,130,53,0.24)] transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loadingLabel || "Verify OTP"}
                    </button>
                  </div>
                </div>
              ) : null}

              <div
                className={
                  showRecaptchaShell
                    ? "mt-4 rounded-[16px] border border-[#efe2d8] bg-[#fcfaf8] px-1 py-2"
                    : "h-0 overflow-hidden"
                }
              >
                <div className="flex justify-center overflow-hidden">
                  <div id="package-recaptcha-container" className="min-h-[78px] w-[304px]" />
                </div>
              </div>

              {!otpVisible ? (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={Boolean(loadingLabel)}
                  className="mt-4 w-full rounded-[14px] border border-[#cfc7c0] bg-white px-4 py-[10px] text-[18px] font-semibold text-[#171717] transition hover:border-[#FE8235] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loadingLabel || "Send OTP"}
                </button>
              ) : null}

              <div className="mt-5 flex items-center gap-3 text-[11px] text-[#8a7669]">
                <div className="h-px flex-1 bg-[#e8ddd4]" />
                <div className="shrink-0 font-medium">Trusted by 50,000+ patients</div>
                <div className="h-px flex-1 bg-[#e8ddd4]" />
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3 text-[11px] text-[#5b5049]">
                <div className="flex items-start gap-2">
                  <span className="mt-[2px] inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[#9a7a64]">
                    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 2.5 13 4.2v3.4c0 3-2.1 5.2-5 5.9-2.9-.7-5-2.9-5-5.9V4.2Z" />
                      <path d="m6.3 7.9 1.1 1.1 2.3-2.4" />
                    </svg>
                  </span>
                  <span>Verified therapists</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-[2px] inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[#9a7a64]">
                    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2.8 7.1 8 3l5.2 4.1" />
                      <path d="M4.4 6.7v5h7.2v-5" />
                    </svg>
                  </span>
                  <span>At-home care</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-[2px] inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[#9a7a64]">
                    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m8 2.7 1.5 3 3.3.5-2.4 2.3.6 3.3L8 10.2 5 11.8l.6-3.3-2.4-2.3 3.3-.5Z" />
                    </svg>
                  </span>
                  <span>4.9 rating</span>
                </div>
              </div>
            </div>

            <div className="mt-5 text-center text-[12px] leading-5 text-[#7a6658]">
              By continuing, you agree to our Terms of Service
              <br />
              and Privacy Policy
            </div>
          </div>
        </div>
      </SectionCard>
    );
  };

  const renderPatientSelectionStep = () => (
    <SectionCard className="overflow-hidden">
      <div className="mx-auto max-w-[700px] px-3 pb-2 pt-2">
        <h2 className="text-[24px] font-semibold leading-none text-[#171717]">Select Patient</h2>
        <p className="mt-3 text-[16px] leading-6 text-[#38332f]">
          Multiple profiles are linked to this number. Choose the correct one to continue.
        </p>

        <div className="mt-6 text-[14px] font-semibold uppercase tracking-[0.08em] text-[#6a625b]">
          Linked Profiles
        </div>

        <div className="mt-3 space-y-4">
          {patients.map((patient, index) => (
            <button
              key={patient.patient_id}
              type="button"
              onClick={() => handlePatientSelection(patient)}
              className="group w-full rounded-[18px] border border-[#d9d1ca] bg-white px-4 py-4 text-left transition hover:border-[#FE8235] hover:shadow-[0_8px_22px_rgba(70,49,35,0.06)]"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#fdf0ea] text-[#e26d20]">
                  <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="10" cy="6.5" r="3.1" />
                    <path d="M4.8 16c.8-2.9 3-4.4 5.2-4.4s4.4 1.5 5.2 4.4" />
                  </svg>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold text-[#171717] sm:text-[16px]">
                    {patient.name || `Patient ${index + 1}`}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-[13px] text-[#2f2a27] sm:text-[14px]">
                    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0 text-[#1f1f1f]" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6.2 3.2c.3-.3.7-.3 1 0l1.3 1.3c.3.3.3.7.1 1l-.7.9c.6 1 1.4 1.8 2.4 2.4l.9-.7c.3-.2.7-.2 1 .1l1.3 1.3c.3.3.3.7 0 1l-.8.8c-.5.5-1.2.7-1.9.5-1.7-.5-3.3-1.5-4.7-2.9-1.4-1.4-2.4-3-2.9-4.7-.2-.7 0-1.4.5-1.9Z" />
                    </svg>
                    <span className="truncate">{formatPhoneDisplay(patient.phone_number || mobileNumber)}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-[#e5ddd7] pt-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fbe8df] px-2.5 py-1 text-[12px] font-medium text-[#d35f18] sm:text-[13px]">
                    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 13.2s3.7-3.4 3.7-6A3.7 3.7 0 0 0 8 3.5 3.7 3.7 0 0 0 4.3 7.2c0 2.6 3.7 6 3.7 6Z" />
                      <circle cx="8" cy="7.2" r="1.3" />
                    </svg>
                    {patient.city || "City unavailable"}
                  </span>
                  <span className="inline-flex min-w-[84px] items-center justify-center rounded-[10px] border border-[#c7beb7] bg-white px-4 py-2 text-[14px] font-semibold text-[#171717] transition group-hover:border-[#171717]">
                    Select
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6 border-t border-[#ddd4cd] pt-6">
          <button
            type="button"
            onClick={handleBack}
            className="w-full rounded-[12px] border border-[#cfc5be] bg-white px-5 py-3 text-[16px] font-medium text-[#171717] transition hover:border-[#171717]"
          >
            Back
          </button>
          <div className="mt-4 flex items-center gap-2 text-[14px] text-[#57514c]">
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#bfb6af] text-[10px] font-semibold text-[#716962]">
              i
            </span>
            <span>Multiple patients are linked to this mobile number</span>
          </div>
        </div>
      </div>
    </SectionCard>
  );

  const renderLeadStep = () => (
    <SectionCard>
      <h2 className="text-2xl font-semibold text-[#222]">Create Lead</h2>
      <p className="mt-2 text-sm text-[#616161]">
        No patient profile exists yet. Add the minimum details to create a lead and continue to package selection.
      </p>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Field
          label="Name"
          value={leadForm.name}
          onChange={(event) => setLeadForm((prev) => ({ ...prev, name: event.target.value }))}
          placeholder="Patient name"
        />
        <Field
          label="City"
          value={leadForm.city}
          onChange={(event) => setLeadForm((prev) => ({ ...prev, city: event.target.value }))}
          placeholder="City"
        />
        <Field
          label="Pincode"
          value={leadForm.pincode}
          onChange={(event) =>
            setLeadForm((prev) => ({
              ...prev,
              pincode: event.target.value.replace(/\D+/g, "").slice(0, 6),
            }))
          }
          placeholder="6 digit pincode"
          inputMode="numeric"
          maxLength={6}
        />
        <StaticField label="Mobile Number" value={formatPhoneDisplay(mobileNumber)} />
      </div>
      <div className="mt-4 flex gap-3">
        <SecondaryButton onClick={handleBack}>Back</SecondaryButton>
        <PrimaryButton onClick={handleLeadCreate} disabled={loadingLabel}>
          {loadingLabel || "Save and Continue"}
        </PrimaryButton>
      </div>
    </SectionCard>
  );

  const renderPackageStep = () => {
    const selectedPackageData =
      packages.find((item) => String(item.id) === String(selectedPackageId)) || null;
    const selectedPackageSavings = selectedPackageData
      ? Math.max(
        0,
        Number(selectedPackageData.original_price || 0) -
        Number(selectedPackageData.discounted_price || 0)
      )
      : 0;

    return (
      <SectionCard className="overflow-hidden">
        <div className="relative rounded-[30px] p-4 sm:p-5">
          <div className="pointer-events-none absolute inset-x-12 top-0 h-24 rounded-b-[48px] bg-[radial-gradient(circle_at_top,rgba(255,173,112,0.2),transparent_72%)]" />

          <div className="relative flex items-center gap-2.5">
            <button
              type="button"
              onClick={handleBack}
              className="flex h-6 w-6 shrink-0 items-center justify-center text-[#1e1e1e]"
              aria-label="Back"
            >
              <img src={Back} alt="" className="h-4 w-4" />
            </button>
            <div className="min-w-0 text-[13px] font-semibold uppercase tracking-[0.12em] text-[#2d2a27]">
              Package Booking
            </div>
          </div>

          <div className="relative mt-5">
            <h2 className="text-[26px] font-semibold leading-[1.02] tracking-[-0.04em] text-[#161616] sm:text-[30px]">
              Choose your package
            </h2>
            <p className="mt-1.5 text-[12px] font-medium text-[#47423d]">
              Select the plan that works best for you
            </p>
          </div>

          <div className="relative mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm font-semibold uppercase tracking-[0.12em] text-[#262626]">
              Packages
            </div>
            {/* <div className="rounded-full border border-[#f1ddd2] bg-white/80 px-4 py-2 text-xs text-[#64584f] shadow-[0_10px_24px_rgba(95,72,56,0.05)]">
              <span className="font-semibold text-[#1f1f1f]">{activeActor?.name || "Patient"}</span>
              <span className="mx-2 text-[#c8b4a5]">â€¢</span>
              <span>{formatPhoneDisplay(activeActor?.phone_number || mobileNumber)}</span>
            </div> */}
          </div>

          <div className="relative mt-4 grid gap-3 md:grid-cols-2">
            {packages.map((item) => {
              const isSelected = String(selectedPackageId) === String(item.id);
              const sessionCount = Number(item.no_of_sessions || 0);
              const sessionCost =
                sessionCount > 0
                  ? Number(item.discounted_price || 0) / sessionCount
                  : Number(item.discounted_price || 0);
              const discountPercent =
                Number(item.original_price || 0) > 0
                  ? Math.round(
                    ((Number(item.original_price || 0) - Number(item.discounted_price || 0)) /
                      Number(item.original_price || 0)) *
                    100
                  )
                  : 0;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedPackageId(String(item.id))}
                  className={`group relative overflow-hidden rounded-[20px] border text-left transition duration-200 ${isSelected
                    ? "border-[#FE8235] bg-[linear-gradient(180deg,#ffffff_0%,#fff6ef_100%)] shadow-[0_0_0_1px_rgba(254,130,53,0.12),0_18px_34px_rgba(254,130,53,0.16)]"
                    : "border-[#dfd6cf] bg-[linear-gradient(180deg,#ffffff_0%,#fbf9f7_100%)] shadow-[0_10px_26px_rgba(58,39,24,0.06)] hover:border-[#f1a16b] hover:shadow-[0_14px_28px_rgba(83,57,35,0.1)]"
                    }`}
                >
                  <div
                    className={`absolute inset-x-0 top-0 h-8 ${isSelected
                      ? "bg-[linear-gradient(180deg,rgba(255,196,153,0.3),transparent)]"
                      : "bg-[linear-gradient(180deg,rgba(255,255,255,0.85),transparent)]"
                      }`}
                  />
                  {discountPercent > 0 ? (
                    <div className="absolute right-3 top-0 rounded-b-xl border-x border-b border-[#ffd5ba] bg-[#fff0e4] px-2.5 py-1 text-[11px] font-semibold text-[#ef6d1a]">
                      {discountPercent}% off
                    </div>
                  ) : null}
                  <div className="relative p-3.5">
                    <div className="text-sm text-[#222]">{sessionCount} sessions</div>
                    <div className="mt-1.5 flex items-end justify-between gap-3">
                      <div className="text-[34px] font-semibold leading-none tracking-[-0.04em] text-[#eb5f12]">
                        {formatCurrency(item.discounted_price)}
                      </div>
                      <div className="pb-1 text-sm font-medium text-[#6b625b] line-through">
                        {formatCurrency(item.original_price)}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between border-t border-[#ece3dd] pt-2.5">
                      <div className="text-sm text-[#443d38]">Per session</div>
                      <div className="text-base font-semibold text-[#1f1f1f]">
                        {formatCurrency(sessionCost)}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="relative mt-4 rounded-[22px] border border-[#ddd2ca] bg-[linear-gradient(180deg,#ffffff_0%,#faf7f4_100%)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
            <div className="text-sm font-medium text-[#2e2a27]">Start date</div>
            <div className="mt-2 max-w-[216px]">
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                min={todayInputValue()}
                className="w-full rounded-[14px] border border-[#ccc2bb] bg-white px-4 py-3 text-base text-[#1f1f1f] shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] outline-none transition focus:border-[#FE8235]"
              />
            </div>

            <div className="mt-3 text-sm text-[#473f39]">Session frequency</div>

            <div className="mt-2 grid grid-cols-2 gap-2">
              {FREQUENCY_OPTIONS.filter((option) => option.value !== "custom").map((option) => {
                const isActive = frequencyChoice === option.value;

                return (
                  <button
                    key={String(option.value)}
                    type="button"
                    onClick={() => setFrequencyChoice(option.value)}
                    className={`min-h-[92px] rounded-[14px] border px-4 py-3 text-center transition ${isActive
                      ? "border-[#FE8235] bg-[linear-gradient(180deg,#fff7f0_0%,#ffefe2_100%)] shadow-[0_10px_22px_rgba(254,130,53,0.12)]"
                      : "border-[#cbc1ba] bg-white hover:border-[#FE8235]"
                      }`}
                  >
                    <div className="flex min-h-[68px] flex-col items-center justify-center">
                      <div className="text-[15px] font-semibold leading-tight text-[#1f1f1f]">
                        {option.label}
                      </div>
                      <div className="mt-1 text-xs leading-tight text-[#6b635d]">
                        {option.helper}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFrequencyChoice("custom")}
                className={`min-h-[92px] rounded-[14px] border px-4 py-3 text-center transition ${frequencyChoice === "custom"
                  ? "border-[#FE8235] bg-[linear-gradient(180deg,#fff7f0_0%,#ffefe2_100%)] shadow-[0_10px_22px_rgba(254,130,53,0.12)]"
                  : "border-[#cbc1ba] bg-white hover:border-[#FE8235]"
                  }`}
              >
                <div className="flex min-h-[68px] flex-col items-center justify-center">
                  <div className="text-[15px] font-semibold leading-tight text-[#1f1f1f]">Custom</div>
                  <div className="mt-1 text-xs leading-tight text-[#6b635d]">your cadence</div>
                </div>
              </button>

              {frequencyChoice === "custom" ? (
                <label className="flex min-h-[92px] flex-col justify-center rounded-[14px] border border-[#ccc2bb] bg-white px-3 py-2 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6b635d]">
                    Sessions Per Week
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={7}
                    value={customSessionsPerWeek}
                    onChange={(event) =>
                      setCustomSessionsPerWeek(
                        Math.max(1, Math.min(7, Number(event.target.value || 1)))
                      )
                    }
                    className="mt-2 w-full border-0 bg-transparent px-0 py-0 text-base font-semibold text-[#1f1f1f] outline-none"
                  />
                </label>
              ) : (
                <div />
              )}
            </div>
          </div>

          {selectedPackageData ? (
            <div className="relative mt-4 rounded-[16px] border border-[#f4b081] bg-[linear-gradient(180deg,#fff8f2_0%,#fff2e8_100%)] px-4 py-3 shadow-[0_12px_28px_rgba(254,130,53,0.1)]">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-[#8b4d1d]">
                    {selectedPackageData.no_of_sessions} sessions selected
                  </div>
                  <div className="mt-1 text-sm font-semibold text-[#368f16]">
                    You save {formatCurrency(selectedPackageSavings)}
                  </div>
                </div>
                <div className="text-[30px] font-semibold leading-none tracking-[-0.04em] text-[#ea6519]">
                  {formatCurrency(selectedPackageData.discounted_price)}
                </div>
              </div>
            </div>
          ) : null}

          <div className="relative mt-4 flex gap-3">
            <div className="basis-[30%]">
              <SecondaryButton onClick={handleBack}>Back</SecondaryButton>
            </div>
            <div className="basis-[70%]">
              <PrimaryButton onClick={handleBuildReview} disabled={loadingLabel}>
                {loadingLabel || "Review purchase"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </SectionCard>
    );
  };

  const renderReviewStep = () => {
    const review = reviewData?.review;
    const patientView = review?.patient || review?.lead || {};
    const packageView = review?.package || {};
    const schedule = review?.schedule || {};
    const selectedPackageMeta =
      packages.find((item) => String(item.id) === String(packageView.id || selectedPackageId)) || {};
    const packageSessions = packageView.sessions || selectedPackageMeta.no_of_sessions || "--";
    const discountedAmount = Number(
      packageView.discounted_price || selectedPackageMeta.discounted_price || 0
    );
    const originalAmount = Number(
      packageView.original_price || selectedPackageMeta.original_price || discountedAmount
    );
    const discountPercent =
      originalAmount > discountedAmount
        ? Math.round(((originalAmount - discountedAmount) / originalAmount) * 100)
        : 0;
    const amountToPay = formatCurrency(discountedAmount);

    return (
      <SectionCard className="overflow-hidden">
        <div className="relative rounded-[30px] p-4 sm:p-5">
          <div className="pointer-events-none absolute inset-x-12 top-0 h-24 rounded-b-[48px] bg-[radial-gradient(circle_at_top,rgba(255,173,112,0.16),transparent_72%)]" />

          <div className="relative flex items-center gap-2.5">
            <button
              type="button"
              onClick={handleBack}
              className="flex h-6 w-6 shrink-0 items-center justify-center text-[#1e1e1e]"
              aria-label="Back"
            >
              <img src={Back} alt="" className="h-4 w-4" />
            </button>
            <div className="min-w-0 text-[13px] font-semibold uppercase tracking-[0.12em] text-[#2d2a27]">
              Package Booking
            </div>
          </div>

          <div className="relative mt-5">
            <h2 className="text-[26px] font-semibold leading-[1.02] tracking-[-0.04em] text-[#161616] sm:text-[30px]">
              Review and pay
            </h2>
            <p className="mt-1.5 text-[12px] font-medium text-[#47423d]">
              Confirm your package and payment details
            </p>
          </div>

          <div className="mt-5 w-[calc(100%+16px)] -mx-2 sm:w-[calc(100%+24px)] sm:-mx-3">
            <div className="rounded-[22px] border border-[#f0a678] bg-[linear-gradient(180deg,rgba(255,255,255,0.94)_0%,rgba(255,245,237,0.98)_100%)] px-4 py-3 shadow-[0_10px_24px_rgba(254,130,53,0.08)]">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#7b726d]">
                    Package Booking
                  </div>
                  <div className="mt-1 text-[28px] font-semibold leading-none tracking-[-0.03em] text-[#181818]">
                    {packageSessions} Sessions
                  </div>
                  <div className="mt-2 text-sm text-[#6a615a]">
                    {packageView.package_name || "Selected package"}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[24px] font-semibold leading-none tracking-[-0.04em] text-[#ea6519]">
                    {amountToPay}
                  </div>
                  <div className="mt-2 flex items-center justify-end gap-2">
                    {originalAmount > discountedAmount ? (
                      <span className="text-xs font-medium text-[#7e746e] line-through">
                        {formatCurrency(originalAmount)}
                      </span>
                    ) : null}
                    {discountPercent > 0 ? (
                      <span className="rounded-full bg-[#dbf7dd] px-2 py-1 text-[11px] font-semibold text-[#187d2f]">
                        {discountPercent}% Off
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {review?.active_package_exists ? (
              <div className="mt-4 rounded-[20px] border border-[#f7c79b] bg-[#fff4ea] px-4 py-3 text-sm text-[#8c4d18]">
                {review.active_package_message}
              </div>
            ) : null}

            <div className="mt-5 rounded-[24px] border border-[#ece1d8] bg-white/75 p-5">
              <div className="text-base font-semibold text-[#1f1f1f]">Patient details</div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field
                  label="Name"
                  value={reviewForm.patient_name}
                  onChange={(event) =>
                    setReviewForm((prev) => ({ ...prev, patient_name: event.target.value }))
                  }
                />
                <StaticField
                  label="Mobile"
                  value={formatPhoneDisplay(patientView.phone_number || mobileNumber)}
                />
                <Field
                  label="City"
                  value={reviewForm.city}
                  onChange={(event) =>
                    setReviewForm((prev) => ({ ...prev, city: event.target.value }))
                  }
                />
                <Field
                  label="Pincode"
                  value={reviewForm.pincode}
                  onChange={(event) =>
                    setReviewForm((prev) => ({
                      ...prev,
                      pincode: event.target.value.replace(/\D+/g, "").slice(0, 6),
                    }))
                  }
                  inputMode="numeric"
                  maxLength={6}
                />
              </div>
            </div>

            <div className="mt-4 rounded-[24px] border border-[#ece1d8] bg-white/75 p-5">
              <div className="text-base font-semibold text-[#1f1f1f]">Package and schedule</div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <ReviewField label="Sessions" value={packageView.sessions || "--"} />
                <ReviewField label="Session cost" value={formatCurrency(packageView.per_session_amount)} />
                <ReviewField label="Start date" value={formatDate(schedule.start_date)} />
                <ReviewField label="End date" value={formatDate(schedule.preview_end_date)} />
                <ReviewField label="Sessions / week" value={schedule.sessions_per_week || "--"} />
                <ReviewField label="Pattern" value={schedule.frequency_label || "--"} />
              </div>
            </div>

            <div className="mt-4 rounded-[24px] border border-[#ece1d8] bg-white/75 p-5">
              <div className="text-base font-semibold text-[#1f1f1f]">Treatment address</div>
              <div className="mt-4 grid gap-4">
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-[#4a4a4a]">Address</span>
                  {isPlacesLoaded ? (
                    <Autocomplete
                      onLoad={handleAddressAutocompleteLoad}
                      onPlaceChanged={handleAddressPlaceChanged}
                    >
                      <input
                        type="text"
                        value={reviewForm.address}
                        onChange={(event) =>
                          setReviewForm((prev) => ({ ...prev, address: event.target.value }))
                        }
                        placeholder="Full address for treatment delivery"
                        className="w-full rounded-2xl border border-[#e7ddd5] bg-white px-4 py-3 text-sm text-[#222] outline-none transition focus:border-[#FE8235]"
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      type="text"
                      value={reviewForm.address}
                      onChange={(event) =>
                        setReviewForm((prev) => ({ ...prev, address: event.target.value }))
                      }
                      placeholder="Full address for treatment delivery"
                      className="w-full rounded-2xl border border-[#e7ddd5] bg-white px-4 py-3 text-sm text-[#222] outline-none transition focus:border-[#FE8235]"
                    />
                  )}
                </label>
                <Field
                  label="Notes (Optional)"
                  value={reviewForm.notes}
                  onChange={(event) =>
                    setReviewForm((prev) => ({ ...prev, notes: event.target.value }))
                  }
                  placeholder="Any care instructions or location notes"
                  textarea
                />
              </div>
            </div>

            <div className="mt-4 rounded-[24px] border border-[#ece1d8] bg-white/75 p-5">
              <div className="text-base font-semibold text-[#1f1f1f]">Terms and activation</div>
              <div className="mt-4 space-y-3">
                {TERMS.map((item, index) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#f4efe9] text-[11px] font-semibold text-[#7d736b]">
                      {index + 1}
                    </div>
                    <div className="text-sm leading-6 text-[#5c534d]">{item}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 flex w-[calc(100%+16px)] -mx-2 gap-3 sm:w-[calc(100%+24px)] sm:-mx-3">
            <div className="basis-[30%]">
              <SecondaryButton onClick={handleBack}>Back</SecondaryButton>
            </div>
            <div className="basis-[70%]">
              <PrimaryButton onClick={handlePayment} disabled={loadingLabel}>
                {loadingLabel || `Pay ${amountToPay}`}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </SectionCard>
    );
  };

  const renderSuccessStep = () => {
    const patientName = successData?.patient_name || reviewForm.patient_name || activeActor?.name || "--";
    const patientMobile = formatPhoneDisplay(
      reviewData?.review?.patient?.phone_number ||
      reviewData?.review?.lead?.phone_number ||
      activeActor?.phone_number ||
      mobileNumber
    );
    const packageName = successData?.package_name || reviewData?.review?.package?.package_name || "--";
    const amountPaid = formatCurrency(successData?.amount_paid);
    const packageStatus = successData ? "Confirmed" : "--";
    const paymentReference = successData?.payment_reference_number || "--";
    const startDateValue = formatDate(reviewData?.review?.schedule?.start_date);
    const addressValue = reviewForm.address || "--";

    return (
      <SectionCard className="pt-3">
        <div className="mx-auto max-w-[680px]">
          <div className="mt-5 rounded-[20px] bg-white px-3 py-3 shadow-[0_2px_6px_rgba(0,0,0,0.05)]">
            <div className="flex items-center">
              <img src={checkIcon} alt="Confirmed" className="mr-[10px] h-[30px] w-[30px] object-contain" />
              <span className="text-[20px] font-bold text-[#222]">Package Purchased!</span>
            </div>
          </div>

          <div className="px-[10px]">
            <h3 className="mb-[15px] mt-[15px] text-[17px] font-bold leading-[21.79px] text-[#111]">
              {packageName}
            </h3>

            <div className="mb-[15px] ml-[10px] flex justify-between gap-4">
              <div className="flex-1">
                <span className="block text-[14px] font-semibold text-[#999]">Name</span>
                <span className="text-[14px] font-bold text-[#111]">{patientName}</span>
              </div>
              <div className="ml-[14px] flex-1">
                <span className="block text-[14px] font-semibold text-[#999]">Mobile</span>
                <span className="text-[14px] font-bold text-[#111]">{patientMobile}</span>
              </div>
            </div>

            <div className="mb-[15px] ml-[10px] flex justify-between gap-4">
              <div className="flex-1">
                <span className="block text-[14px] font-semibold text-[#999]">Package Status</span>
                <span className="text-[14px] font-bold text-[#111]">{packageStatus}</span>
              </div>
              <div className="ml-[14px] flex-1">
                <span className="block text-[14px] font-semibold text-[#999]">Fees</span>
                <span className="text-[14px] font-bold text-[#111]">{amountPaid}</span>
              </div>
            </div>

            <div className="mt-3 w-full border-t border-[#ddd] pt-3">
              <div className="flex items-center">
                <img
                  src={require("../assets/images/calendar.png")}
                  alt="calendar"
                  className="mb-1 mr-[10px] mt-1 h-[22px] w-[22px] object-contain [filter:brightness(0)_saturate(100%)_invert(58%)_sepia(90%)_saturate(1117%)_hue-rotate(347deg)_brightness(100%)_contrast(92%)]"
                />
                <div>
                  <div className="text-[14px] font-semibold text-[#888]">Start Date</div>
                  <div className="mt-[2px] text-[15px] font-semibold text-[#111]">{startDateValue}</div>
                </div>
              </div>
            </div>

            <div className="mt-3 w-full border-t border-[#ddd] pt-3">
              <div className="flex items-center">
                <img
                  src={require("../assets/images/location.png")}
                  alt="location"
                  className="mb-1 mr-[10px] mt-1 h-[22px] w-[22px] object-contain [filter:brightness(0)_saturate(100%)_invert(58%)_sepia(90%)_saturate(1117%)_hue-rotate(347deg)_brightness(100%)_contrast(92%)]"
                />
                <div>
                  <div className="text-[14px] font-semibold text-[#888]">Complete Address</div>
                  <div className="mt-[2px] text-[15px] font-semibold text-[#111]">{addressValue}</div>
                </div>
              </div>
            </div>

            <hr className="my-[15px] h-px border-0 bg-[#ddd]" />

            <div className="mt-5 rounded-[10px] bg-[#f2f2f2] p-3 text-center">
              <p className="text-[12px] font-semibold leading-5 text-[#666]">
                Your package purchase is confirmed. Our team will share any further updates on WhatsApp or call if anything is needed.
              </p>
            </div>

            <div className="mb-5 mt-5 flex flex-col items-center justify-center">
              <span className="text-[14px] font-semibold text-[#999]">Payment Reference</span>
              <span className="text-[18px] font-semibold text-[#111]">{paymentReference}</span>
            </div>

            <WhatHappensNext />

            <button
              type="button"
              className="mx-auto mt-5 block w-1/2 rounded-[8px] bg-[linear-gradient(90deg,#ffab6f_0%,#FE8235_100%)] px-[6px] py-2 text-[16px] font-bold text-white shadow-[0_16px_32px_rgba(254,130,53,0.22)]"
              onClick={() => {
                clearStoredFlow();
                navigate("/");
              }}
            >
              Back To Home
            </button>
          </div>
        </div>
      </SectionCard>
    );
  };

  return (
    <div className={`min-h-screen ${isAuthStep ? "bg-[#fbf6f1]" : "bg-[#f9f2ec]"} px-0 py-0 md:px-4 md:py-6`}>
      <div
        className={`relative mx-auto w-full overflow-hidden ${isAuthStep
          ? "min-h-screen max-w-[760px] bg-cover bg-no-repeat md:min-h-0 md:rounded-[28px] md:shadow-[0_24px_64px_rgba(57,57,56,0.08)]"
          : "min-h-screen max-w-[760px] bg-cover bg-no-repeat md:min-h-0 md:rounded-[28px] md:shadow-[0_24px_64px_rgba(57,57,56,0.12)]"
          }`}
        style={{
          backgroundImage: `url(${require("../assets/images/BackgroundBig.png")})`,
        }}
      >
        <div
          className={`pointer-events-none absolute inset-0 ${isAuthStep ? "bg-white/25 backdrop-blur-[1.5px]" : "bg-white/10 backdrop-blur-[3px]"
            }`}
        />
        <div
          className={`relative z-10 mx-auto ${isAuthStep ? "max-w-[360px] px-3 pb-8 pt-8" : "max-w-[760px] px-4 pb-8 pt-3 md:px-6"
            }`}
        >
          {step !== "mobile" && step !== "otp" && step !== "patient-select" && step !== "packages" && step !== "review" && step !== "success" ? (
            <div className="relative py-3">
              <button
                type="button"
                onClick={handleBack}
                className="absolute left-0 flex h-6 w-6 items-center justify-center text-[#1e1e1e]"
                aria-label="Back"
              >
                <img src={Back} alt="" className="h-4 w-4" />
              </button>
            </div>
          ) : null}

          {firebaseMissing ? (
            <SectionCard>
              <h2 className="text-2xl font-semibold text-[#222]">Setup Required</h2>
              <p className="mt-3 text-sm text-[#616161]">
                Firebase web OTP is not configured in this frontend yet. Add the required `REACT_APP_FIREBASE_*`
                variables before using this page.
              </p>
              <div className="mt-4 rounded-2xl bg-[#fff5ef] p-4 text-sm text-[#5d4c3f]">
                <div>`REACT_APP_FIREBASE_API_KEY`</div>
                <div>`REACT_APP_FIREBASE_AUTH_DOMAIN`</div>
                <div>`REACT_APP_FIREBASE_PROJECT_ID`</div>
                <div>`REACT_APP_FIREBASE_MESSAGING_SENDER_ID`</div>
                <div>`REACT_APP_FIREBASE_APP_ID`</div>
                <div className="mt-2">Also ensure the booking domain is authorized in Firebase Authentication.</div>
              </div>
            </SectionCard>
          ) : null}

          {!firebaseMissing && (step === "mobile" || step === "otp") ? renderMobileStep() : null}
          {!firebaseMissing && step === "patient-select" ? renderPatientSelectionStep() : null}
          {!firebaseMissing && step === "lead" ? renderLeadStep() : null}
          {!firebaseMissing && step === "packages" ? renderPackageStep() : null}
          {!firebaseMissing && step === "review" ? renderReviewStep() : null}
          {!firebaseMissing && step === "success" ? renderSuccessStep() : null}

          {lookupSnapshot?.resolution && step !== "success" && step !== "patient-select" ? (
            <div className="mt-4 flex items-center gap-2 text-[11px] font-semibold text-[#7a7a7a]">
              <span className="flex h-4 w-4 items-center justify-center rounded-full border border-[#cfc6bf] text-[10px] font-bold text-[#8a827c]">
                i
              </span>
              <span>
                {lookupSnapshot.resolution.replace(/_/g, " ")}
                {loginSnapshot?.verified === false ? " | new login detected" : ""}
              </span>
            </div>
          ) : null}

          {errorMessage && !showFailModel ? (
            <div className="mt-4 rounded-2xl border border-[#f3c1bd] bg-[#fff0ef] px-4 py-3 text-sm text-[#b42318]">
              {errorMessage}
            </div>
          ) : null}

          {showFailModel ? (
            <FailModel
              onClose={closeFailModel}
              errorMessage={errorMessage}
              introMessage="Your package payment could not be completed."
              outroMessage="Please try again. If money was debited, contact our team and share the payment reference."
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

const SectionCard = ({ children, className = "" }) => (
  <div
    className={`mt-4 p-1 md:p-2 ${className}`}
  >
    {children}
  </div>
);

const Field = ({ label, textarea = false, ...props }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-semibold text-[#4a4a4a]">{label}</span>
    {textarea ? (
      <textarea
        {...props}
        rows={4}
        className="min-h-[120px] w-full rounded-2xl border border-[#e7ddd5] bg-white px-4 py-3 text-sm text-[#222] outline-none transition focus:border-[#FE8235]"
      />
    ) : (
      <input
        {...props}
        className="w-full rounded-2xl border border-[#e7ddd5] bg-white px-4 py-3 text-sm text-[#222] outline-none transition focus:border-[#FE8235]"
      />
    )}
  </label>
);

const StaticField = ({ label, value }) => (
  <div>
    <div className="mb-2 text-sm font-semibold text-[#4a4a4a]">{label}</div>
    <div className="rounded-2xl border border-[#ece2db] bg-[#faf7f4] px-4 py-3 text-sm text-[#444]">
      {value}
    </div>
  </div>
);

const PrimaryButton = ({ children, disabled, onClick }) => (
  <button
    type="button"
    disabled={Boolean(disabled)}
    onClick={onClick}
    className="h-14 w-full whitespace-nowrap rounded-2xl bg-[linear-gradient(90deg,#ffab6f_0%,#FE8235_100%)] px-5 text-sm font-semibold text-white shadow-[0_16px_32px_rgba(254,130,53,0.25)] transition disabled:cursor-not-allowed disabled:opacity-60"
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-14 w-full whitespace-nowrap rounded-2xl border border-[#e3d7cf] bg-white px-5 text-sm font-semibold text-[#4a4a4a] transition hover:border-[#FE8235]"
  >
    {children}
  </button>
);

const ReviewField = ({ label, value }) => (
  <div className="border-b border-[#efe5dc] pb-3 last:border-b-0 last:pb-0">
    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8b817a]">
      {label}
    </div>
    <div className="mt-1 text-[15px] font-semibold text-[#1d1d1d]">
      {value}
    </div>
  </div>
);

export default PackagePurchaseFlow;
